import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import 'rxjs/add/operator/map';
import * as _ from 'lodash';

import { PagedResponse } from '@models/response.model';
import { ToolReturn } from "@models/tool-return.model";

import { TreeModel } from '@components/tree/tree.model';

import { TableConfig } from "@app/shared/util/table-config.utils";
import { AppUtils } from "@app/shared/util/app.utils";

import { environment } from 'environments/environment';
import { Group } from '@app/tool/model/group.model';
import { GroupChildWrapper } from '@app/tool/model/group-child-wrapper.model';
import { Section } from "@models/enums/section.utils";
import { Tool } from '@app/tool/model/tool.model';
import { ToolPosition } from '@app/tool/model/tool-position.model';
import { Settings } from "@app/settings/models/settings.model";
import { map } from "rxjs/operators";

/**
 * Service class for handling CRUD operations on {@link ToolReturn} objects.
 */
@Injectable()
export class ToolReturnsService {
    static readonly API_TOOL_RETURNS_BASE_PATH = `${environment.baseURL}/api/toolReturn`;
    static readonly API_TOOL_RETURNS_FOR_MAINTENANCE_BASE_PATH = `${environment.baseURL}/api/maintenance`;
    static readonly API_SETTINGS_PATH = `${environment.baseURL}/api/settings`;

    constructor(private http: HttpClient) {
    }


    getAllToolReturns(filter: TableConfig, type: string = 'LINE', section: Section) {
        let params = AppUtils.buildPageableParamsFromList(filter);
        params = params.set('type', type);
        params = params.set('section', section ? section.toString() : null);

        return this.http.get<PagedResponse<ToolReturn[]>>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}`,
            {params: params})
            .pipe(map(this.mapToolReturns));
    }

    getToolReturn(id: number, includeRemovedTools = false) {
        return this.http.get<PagedResponse<ToolReturn>>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${id}?includeRemovedTools=${includeRemovedTools}`)
            .pipe(map(this.mapToolReturn));
    }

    getToolReturnByToolRequestId(toolRequestId: number) {
        return this.http.get<PagedResponse<ToolReturn>>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolRequestId}/toolRequest`)
            .pipe(map(this.mapToolReturn));
    }

    getToolReturnRequestDetails(toolReturn: ToolReturn) {
        return this.http.get<PagedResponse<ToolReturn>>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturn.id}`)
            .pipe(map(this.mapToolReturn));
    }

    createToolReturn(toolReturn) {
        return this.http.post<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}`, toolReturn)
            .pipe(map(this.mapToolReturn));
    }

    createToolReturnForMaintenance(toolReturn) {
        return this.http.post<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_FOR_MAINTENANCE_BASE_PATH}`, toolReturn)
            .pipe(map(this.mapToolReturn));
    }

    approveToolReturnForMaintenance(toolReturnId, body) {
        body.TicketAtService = this.adaptTicket(body.TicketAtService);
        body.TicketAtLine = this.adaptTicket(body.TicketAtLine);
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_FOR_MAINTENANCE_BASE_PATH}/
                                            ${toolReturnId}/approve`, body);
    }

    startProcessingToolReturn(id: number) {
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${id}/startProcessing`, {})
    }

    closeToolReturnById(id: number, boschId: string) {
        let headers = new HttpHeaders();
        if (boschId) {
            headers = headers.set("BoschId", boschId);
        }
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${id}/close`, {}, {headers: headers})
    }

    processToolReturn(toolReturn: ToolReturn) {
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturn.id}/startApprove`, null);
    }

    approveToolReturn(toolReturn: ToolReturn, isApprovedElsewhereThanLift: boolean) {
        const params = new HttpParams().set('isApprovedElsewhereThanLift', `${isApprovedElsewhereThanLift}`);
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturn.id}/approve`, null,
            {params: params});
    }

    confirmToolScan(toolReturn: ToolReturn, tool: TreeModel) {
        let params = new HttpParams();

        params = params.set('toolId', tool.data.id);

        return this.http.post<ToolReturn>(
            `${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturn.id}/confirmationScan`,
            null, {params: params}
        );
    }


    getToolReturnTools(toolReturnId: number) {
        let params = new HttpParams();
        params = params.set('LineId', toolReturnId + '');
        return this.http.get<PagedResponse<Tool[]>>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/tools`,
            {params: params})
            .pipe(map(this.mapGroups));
    }

    getSettings() {
        return this.http.get<Settings[]>(`${ToolReturnsService.API_SETTINGS_PATH}`);
    }

    cancelToolReturnRequest(toolReturn: ToolReturn) {
        return this.http.patch<ToolReturn>(`${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturn.id}/cancel`,
            {closeReason: toolReturn.reason});
    }

    assignToolToSlot(toolReturnId: number, toolId: number, position: ToolPosition) {
        return this.http.put<ToolReturn>(
            `${ToolReturnsService.API_TOOL_RETURNS_BASE_PATH}/${toolReturnId}/tools/${toolId}/position`, position);
    }

    private mapToolReturns = res => {
        return {
            items: res.items.map(toolReturn => new ToolReturn(toolReturn)),
            total: res.total
        };
    };

    private mapToolReturn = res => {
        return new ToolReturn(res);
    };

    private mapGroupableItems(items) {
        const groups = items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
        return groups;
    }

    private mapGroups = res => {
        const body = res;
        return {
            items: this.mapGroupableItems(body.items),
            total: body.total
        };
    };

    adaptTicket(ticket) {
        if (!Object.keys(ticket).length) {
            return null;
        }
        return {
            description: ticket.description,
            operationIds: _.map(ticket.operationTypes, 'id'),
            ticketOperations: ticket.ticketOperations,
            hasPriority: ticket.hasPriority,
            toolIds: ticket.issueId ? _.map(ticket.selectedTools, 'id') : this.adaptTools(ticket.selectedTools),
            issueId: ticket.issueId,
            hasMaintenanceAtLine: ticket.hasMaintenanceAtLine || false,
            lineToolRequestId: ticket.lineToolRequestId,
            type: ticket.type || 0,
            lineId: ticket.lineId
        }
    }

    adaptTools(selectedTools) {
        _.remove(selectedTools, (someObj: TreeModel) => {
            return someObj.data && someObj.data.length > 1 && someObj.data.id == null;
        });

        return _.map(selectedTools, 'data.id');
    }

}
